import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye, QrCode, FileJson, FileSpreadsheet as FileSpreadsheetIcon } from 'lucide-react'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import type { Object as ObjectEntity } from '@/types/entities'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { getResponsiveColumnSize } from '@/lib/utils/responsive-columns'
import { buildIconPath } from '@/lib/imagePaths'
import { TranslatableText } from '@/components/ui/TranslatableText'

interface ObjectActionsProps {
    object: ObjectEntity
    onEdit: (object: ObjectEntity) => void
    onDelete: (object: ObjectEntity) => void
    onView: (object: ObjectEntity) => void
}

function ObjectActions({ object, onEdit, onDelete, onView }: ObjectActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">{t('objects.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                    onCloseAutoFocus={(e) => {
                        e.preventDefault()
                    }}
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onView(object)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('objects.viewObject')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(object)
                            setOpen(false)
                        }}
                    >
                        <Edit className="mr-2 h-4 w-4" />
                        {t('objects.editObject')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(object)
                            setOpen(false)
                        }}
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('objects.deleteObject')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

export function createObjectColumns(
    onDelete: (object: ObjectEntity) => void,
    onEdit: (object: ObjectEntity) => void,
    onView: (object: ObjectEntity) => void,
    onShowQRCode: (objectID: string) => void,
    onShowMetadata?: (objectID: string) => void,
    t: (key: string) => string,
    isService: boolean = false
): ColumnDef<ObjectEntity>[] {
    // Calculate responsive column sizes (Object ID column holds ID + description)
    const columnSizes = {
        objectID: getResponsiveColumnSize(280),  // Width for Object ID + description
        objectIcon: 40,  // Fixed width for object icon
        qrCode: 60,  // Fixed width for QR code icon
        metadata: 40,  // Fixed width for metadata icon
        status: getResponsiveColumnSize(70),
        location: getResponsiveColumnSize(130),
        engineerRTO: 85,  // Fixed width to fit header text
        endToEndTime: 85,  // Fixed width to fit header text
        totalRTO: 85,  // Fixed width to fit header text
        imported: 85,  // Fixed width for Imported column
        actions: 80  // Fixed width, doesn't scale
    }

    return [
        {
            accessorKey: 'objectID',
            header: t('objects.objectID'),
            size: columnSizes.objectID,
            minSize: 200,
            maxSize: 400,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const description = row.original.objectDescription
                const hoverText = [objectID, description].filter(Boolean).join(' — ')
                return (
                    <div className="flex flex-col min-w-0" title={hoverText || undefined}>
                        <span className="text-sm font-medium truncate">{objectID || '-'}</span>
                        {description && (
                            <span className="text-xs text-muted-foreground truncate"><TranslatableText text={description} /></span>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'objectIcon',
            header: '',
            size: columnSizes.objectIcon,
            minSize: 40,
            maxSize: 40,
            cell: ({ row }) => {
                const iconFilename = row.original.iconFilename
                if (!iconFilename) return null
                return (
                    <div className="flex items-center justify-center">
                        <img
                            src={buildIconPath(iconFilename)}
                            alt=""
                            className="h-8 w-8 object-contain"
                        />
                    </div>
                )
            },
        },
        {
            id: 'qrCode',
            header: '',
            size: columnSizes.qrCode,
            minSize: 60,
            maxSize: 60,
            cell: ({ row }) => {
                const objectID = row.original.objectID
                if (!objectID) return <div>-</div>
                
                return (
                    <div className="flex items-center justify-center">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowQRCode(objectID)
                            }}
                            title={t('objects.showQRCode')}
                        >
                            <QrCode className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        },
        // Metadata column only for Objects (services do not have metadata)
        ...(!isService ? [{
            id: 'metadata',
            header: '',
            size: columnSizes.metadata,
            minSize: 36,
            maxSize: 44,
            cell: ({ row }) => {
                const objectID = row.original.objectID
                if (!objectID || !onShowMetadata) return <div className="flex items-center justify-center p-0">-</div>
                return (
                    <div className="flex items-center justify-center p-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 min-w-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowMetadata(objectID)
                            }}
                            title={t('objects.viewMetadata', 'View metadata')}
                        >
                            <FileJson className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        }] : []),
        {
            accessorKey: 'datastore',
            header: '',
            size: 40,
            minSize: 40,
            maxSize: 40,
            enableHiding: false,
            cell: ({ row }) => {
                const datastore = row.getValue('datastore') as number | null
                // Check if datastore is 1 (Yes) - column is INT, 1 = Yes
                if (datastore === 1) {
                    return (
                        <div className="flex items-center justify-center">
                            <img 
                                src="/images/database.png" 
                                alt="Datastore" 
                                className="h-4 w-4"
                            />
                        </div>
                    )
                }
                return null
            },
        },
        {
            accessorKey: 'objectStatus',
            header: () => <div className="text-center w-full">{t('objects.status')}</div>,
            size: columnSizes.status,
            minSize: 70,
            maxSize: 120,
            cell: ({ row }) => {
                const statusValue = row.getValue('objectStatus') != null ? Number(row.getValue('objectStatus')) : null
                if (statusValue === null) return <div className="flex justify-center">-</div>
                return (
                    <div className="flex items-center justify-center">
                        <AnalyticsStatusIndicator status={statusValue} size="h-4 w-4" />
                    </div>
                )
            },
        },
        {
            accessorKey: 'locationName',
            header: t('objects.locationName'),
            size: columnSizes.location,
            minSize: 120,
            maxSize: 250,
            cell: ({ row }) => {
                const locationName = row.getValue('locationName') as string
                return (
                    <div className="text-sm text-muted-foreground">
                        {locationName ? <TranslatableText text={locationName} /> : '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'engineerRTO',
            header: () => <div className="text-right w-full">{t('objects.engineerRTO')}</div>,
            size: columnSizes.engineerRTO,
            minSize: 80,
            maxSize: 90,
            cell: ({ row }) => {
                const engineerRTO = row.getValue('engineerRTO') as number | null
                return (
                    <div className="text-right text-sm">
                        {engineerRTO !== null && engineerRTO !== undefined ? formatSecondsToDHM(engineerRTO) : '-'}
                    </div>
                )
            },
        },
        ...(!isService ? [{
            accessorKey: 'endToEndTime',
            header: () => <div className="text-right w-full">{t('objects.endToEndTime')}</div>,
            size: columnSizes.endToEndTime,
            minSize: 80,
            maxSize: 90,
            cell: ({ row }) => {
                const endToEndTime = row.getValue('endToEndTime') as number | null
                return (
                    <div className="text-right text-sm">
                        {endToEndTime !== null && endToEndTime !== undefined ? formatSecondsToDHM(endToEndTime) : '-'}
                    </div>
                )
            },
        }] : []),
        {
            accessorKey: 'totalCalculatedRTO',
            header: () => <div className="text-right w-full">{t('objects.rolledUpRTO')}</div>,
            size: columnSizes.totalRTO,
            minSize: 80,
            maxSize: 90,
            cell: ({ row }) => {
                const totalCalculatedRTO = row.getValue('totalCalculatedRTO') as number | null
                return (
                    <div className="text-right text-sm">
                        {totalCalculatedRTO !== null && totalCalculatedRTO !== undefined ? formatSecondsToDHM(totalCalculatedRTO) : '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'imported',
            header: () => <div className="text-center w-full">{t('objects.imported', 'Imported')}</div>,
            size: columnSizes.imported,
            minSize: 80,
            maxSize: 90,
            cell: ({ row }) => {
                const imported = row.getValue('imported') as number | null
                if (imported !== 1) return <div className="flex justify-center">-</div>
                const hasRto = row.original.engineerRTO != null && row.original.engineerRTO > 0
                return (
                    <div className="flex items-center justify-center" title={!hasRto ? t('objects.importedIncomplete') : t('objects.importedComplete')}>
                        {/* amber = setup incomplete, blue = complete */}
                        <FileSpreadsheetIcon className={`h-4 w-4 ${!hasRto ? 'text-amber-500' : 'text-blue-500'}`} />
                    </div>
                )
            },
        },
        {
            id: 'actions',
            size: columnSizes.actions,
            minSize: 80,
            maxSize: 80,
            header: () => (
                <div className="text-center w-full">
                    {t('common.actions')}
                </div>
            ),
            cell: ({ row }) => {
                const object = row.original
                return (
                    <ObjectActions
                        object={object}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        onView={onView}
                    />
                )
            },
        },
    ]
}
